import { useCallback, type Dispatch, type SetStateAction } from "react";
import { useRouter } from "next/navigation";
import type { Member } from "@cabal/shared/domain/session/team/member/types.js";
import type { SessionOverviewResponse } from "@cabal/shared/domain/session/types.js";
import { useApi } from "@/contexts/api";
import { isForbiddenApiError } from "@/utils/session-mutation-errors";
import { resolveUnreachableRouteDestination } from "@/lib/authReturnRoute";
import type { LoadingStates } from "@/types/frontend-types";
import { toMembersMap } from "./initial-load-selectors";
import { SessionUpdateSource } from "./session-data-provider-utils";

type SessionDataApi = ReturnType<typeof useApi>;

interface UseSessionRefreshActionsInput {
  api: SessionDataApi;
  router: ReturnType<typeof useRouter>;
  sessionId: string;
  setLoading: Dispatch<SetStateAction<LoadingStates>>;
  setMembers: Dispatch<SetStateAction<Record<string, Member>>>;
  updateActivitySinceTimestamp: (activitySince: string) => void;
  applySessionFromSource: (input: {
    nextSession: SessionOverviewResponse;
    source: SessionUpdateSource;
  }) => void;
}

interface SessionRefreshActions {
  refreshMembers: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

export function useSessionRefreshActions({
  api,
  router,
  sessionId,
  setLoading,
  setMembers,
  updateActivitySinceTimestamp,
  applySessionFromSource,
}: UseSessionRefreshActionsInput): SessionRefreshActions {
  const refreshMembers = useCallback(async (): Promise<void> => {
    try {
      setLoading((prev) => ({ ...prev, members: true }));

      const envelope = await api.sessions.byId(sessionId).teamMembers();
      setMembers(toMembersMap(envelope.items));
      updateActivitySinceTimestamp(envelope.now);
      setLoading((prev) => ({ ...prev, members: false }));
    } catch {
      setLoading((prev) => ({ ...prev, members: false }));
    }
  }, [api, sessionId, setLoading, setMembers, updateActivitySinceTimestamp]);

  const refreshSession = useCallback(async (): Promise<void> => {
    try {
      const sessionData = await api.sessions.byId(sessionId).overview();
      applySessionFromSource({
        nextSession: sessionData,
        source: SessionUpdateSource.Overview,
      });
    } catch (error) {
      if (isForbiddenApiError(error)) {
        router.replace(resolveUnreachableRouteDestination("/forbidden"));
      }
    }
  }, [api, router, sessionId, applySessionFromSource]);

  return {
    refreshMembers,
    refreshSession,
  };
}
